import React from 'react';
import { useTheme } from '@mui/material';
import { BackendNode } from './types';
import { VIEW_W, FOCUS_VIEW_H, FOCUS_NODE_R, SAT_R, LABEL_EDGE_PAD, LABEL_SLOT_GUTTER, getBgColor } from './layout';
import {
    CenterNode,
    DisabledTooltip,
    LabelTooltip,
    AlertTriangle,
    isReportMember,
    nodeCircleOpacity,
    nodeLabelOpacity,
    reportOutline,
} from './Nodes';
import { fitLabel } from './fitLabel';
import Legend from './Legend';
import { satelliteColor } from './nodeTypeColors';

const CHILD_LABEL_FONT = 10;
// Figma 697-2588 (child-row labels)
const CHILD_LABEL_COLOR = '#040404';
const CENTER_X = VIEW_W / 2;
const CENTER_Y = FOCUS_VIEW_H / 2;
// Up to this many related nodes the ring keeps Figma's compact circle; past it the ring stretches to
// an ellipse that uses the drawing's full width so neighbouring labels stop colliding.
const COMPACT_RING_MAX = 8;
const COMPACT_R = 190;
const WIDE_RX = 330;
const WIDE_RY = 228;
const LABEL_GAP = 10;
const LINE_H = 12;

interface FocusSvgProps {
    focusedNode: BackendNode;
    relatedNodes: BackendNode[];
    typeColors: Map<string, string>;
    hoveredId: number | null;
    reportLensActive?: boolean;
    reportMemberUids?: ReadonlySet<string>;
    // The in-SVG legend is skipped when the host renders LegendStrip below the drawing.
    showLegend?: boolean;
    onHover: (id: number | null) => void;
    onClickRelated: (node: BackendNode) => void;
}

interface RingSlot {
    cx: number;
    cy: number;
    angle: number;
}

function ringSlots(count: number): RingSlot[] {
    const wide = count > COMPACT_RING_MAX;
    const rx = wide ? WIDE_RX : COMPACT_R;
    const ry = wide ? WIDE_RY : COMPACT_R;
    return Array.from({ length: count }, (_, i) => {
        const angle = -Math.PI / 2 + (2 * Math.PI * i) / count;
        return { cx: CENTER_X + rx * Math.cos(angle), cy: CENTER_Y + ry * Math.sin(angle), angle };
    });
}

// Horizontal room a label may take before it runs into its neighbour's slot or the viewBox edge.
function labelBudget(anchor: 'start' | 'middle' | 'end', labelX: number, count: number): number {
    if ('middle' === anchor) {
        const rx = count > COMPACT_RING_MAX ? WIDE_RX : COMPACT_R;
        const neighbourGap = count > 1 ? rx * ((2 * Math.PI) / count) : VIEW_W;
        return Math.max(
            40,
            Math.min(neighbourGap - LABEL_SLOT_GUTTER, 2 * Math.min(labelX, VIEW_W - labelX) - 2 * LABEL_EDGE_PAD),
        );
    }
    return ('start' === anchor ? VIEW_W - labelX : labelX) - LABEL_EDGE_PAD;
}

// Radial lineage view (Figma 935:9906): the focused node in the middle, its related nodes around
// it. No model node — the editor is already scoped to one model (ruled 2026-09-06).
export default function FocusSvg({
    focusedNode,
    relatedNodes,
    typeColors,
    hoveredId,
    reportLensActive = false,
    reportMemberUids,
    showLegend = true,
    onHover,
    onClickRelated,
}: FocusSvgProps) {
    const theme = useTheme();
    const bgColor = getBgColor(theme);
    const focusedDisabled = focusedNode.enabled_in_concierge_ind === 'N';
    const slots = ringSlots(relatedNodes.length);

    return (
        <svg viewBox={`0 0 ${VIEW_W} ${FOCUS_VIEW_H}`} width="100%" overflow="visible" style={{ display: 'block' }}>
            {showLegend && <Legend typeColors={typeColors} reportLensActive={reportLensActive} />}

            {slots.map((slot, i) => (
                <line
                    key={relatedNodes[i].llm_learning_node_id}
                    x1={CENTER_X}
                    y1={CENTER_Y}
                    x2={slot.cx}
                    y2={slot.cy}
                    stroke="#b8c0cc"
                    strokeWidth={1.5}
                />
            ))}

            <circle cx={CENTER_X} cy={CENTER_Y} r={FOCUS_NODE_R + 3} fill={bgColor} />
            <CenterNode
                node={focusedNode}
                cx={CENTER_X}
                cy={CENTER_Y}
                r={FOCUS_NODE_R}
                hovered={hoveredId === focusedNode.llm_learning_node_id}
                disabled={focusedDisabled}
                inReport={isReportMember(focusedNode, reportMemberUids)}
                color={satelliteColor(focusedNode.type, focusedNode.node_color)}
                onHover={onHover}
                onClick={() => {}}
            />

            {relatedNodes.map((n, i) => {
                const { cx, cy, angle } = slots[i];
                const disabled = n.enabled_in_concierge_ind === 'N';
                const hovered = hoveredId === n.llm_learning_node_id;
                const inReport = isReportMember(n, reportMemberUids);
                const circleOpacity = nodeCircleOpacity(disabled, inReport, hovered);

                // Labels run outward along the spoke: beside the node on the flanks, above/below it
                // near the top and bottom of the ring.
                const dx = Math.cos(angle);
                const dy = Math.sin(angle);
                const anchor: 'start' | 'middle' | 'end' = dx > 0.35 ? 'start' : dx < -0.35 ? 'end' : 'middle';
                const labelX = 'middle' === anchor ? cx : cx + dx * (SAT_R + LABEL_GAP);
                const baselineY =
                    'middle' === anchor
                        ? dy > 0
                            ? cy + SAT_R + LABEL_GAP + LINE_H - 4
                            : cy - SAT_R - LABEL_GAP + 2
                        : cy + LINE_H / 3;
                const label = fitLabel(n.name, labelBudget(anchor, labelX, relatedNodes.length), CHILD_LABEL_FONT);

                return (
                    <g
                        key={n.llm_learning_node_id}
                        data-testid="kg-node"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => onHover(n.llm_learning_node_id)}
                        onMouseLeave={() => onHover(null)}
                        onClick={() => onClickRelated(n)}
                    >
                        <circle cx={cx} cy={cy} r={SAT_R + 3} fill={bgColor} />
                        <circle
                            cx={cx}
                            cy={cy}
                            r={SAT_R}
                            fill={satelliteColor(n.type, n.node_color)}
                            fillOpacity={circleOpacity}
                            {...reportOutline(inReport)}
                        />
                        <text
                            x={labelX}
                            y={baselineY}
                            textAnchor={anchor}
                            fontSize={CHILD_LABEL_FONT}
                            fill={CHILD_LABEL_COLOR}
                            fillOpacity={nodeLabelOpacity(disabled, inReport, hovered)}
                        >
                            {label.text}
                        </text>
                        {n.alert && (
                            <AlertTriangle
                                x={cx}
                                y={cy - SAT_R - 6}
                                text={n.alert.text}
                                link={n.alert.link}
                                onHoverNode={onHover}
                            />
                        )}
                        {disabled && hovered && (
                            <DisabledTooltip
                                cx={cx}
                                cy={cy}
                                name={n.name}
                                reasonText={n.type === 'Date' ? (n.alert?.text ?? undefined) : undefined}
                            />
                        )}
                        {!disabled && hovered && label.truncated && <LabelTooltip cx={cx} cy={cy} name={n.name} />}
                    </g>
                );
            })}
        </svg>
    );
}
